import { Fragment, useEffect, useRef, useState, type ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { EstadoError, EstadoVacio } from '@/components/ui/estado-vacio';
import { GrupoDeOpciones } from '@/components/ui/grupo-de-opciones';
import { CambioDePrecio, Precio } from '@/components/ui/precio';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { mensajeDeError } from '@/lib/api/mensaje-de-error';
import { formatoPrecio } from '@/lib/utils/formato-precio';
import { PorQueEstePrecio } from '@/features/pricing-rules/components/por-que-este-precio';
import { historialDePreciosApi } from '../services/historial-de-precios-api';
import { priceProposalsApi } from '../services/price-proposals-api';
import type { PropuestaDelHistorial } from '../types/price-proposals.types';
import type {
  CambioDelHistorial,
  HistorialDePrecios as Historial,
} from '../types/historial-de-precios.types';
import { desdeFilaDeLaBandeja } from '../lib/explicacion-del-precio';
import {
  cuandoEnTexto,
  esElPrecioDeHoy,
  filtrosDelHistorial,
  GRUPO_DE_LA_VIA,
  queHizoElVolver,
  quienOQue,
  sePuedeDeshacer,
  sePuedeVolver,
  textoDeDeshacer,
  textoDelCorte,
  textoDeVolver,
  tienePorQue,
  ultimoDelRegistro,
  type GrupoDeVia,
} from '../lib/historial-de-precios';
import { CUALQUIER_FECHA, enElRango, type RangoDeFechas } from '../lib/rango-de-fechas';
import { TextoRecortable } from './texto-recortable';
import { SelectorDeFechas } from './selector-de-fechas';
import { atributosSoloMira, useSoporteSoloMira } from '@/features/mini-prixia/soporte-solo-mira';

/**
 * El historial único de los cambios de precio de un producto (lote C2b, spec
 * 246 punto 11; maqueta `c2-historial`): cuándo, antes → después, el cambio,
 * quién o qué y el motivo. Se filtra por vía y por fecha. Lo de antes del
 * registro va debajo de un corte que dice que está incompleto. Las filas de
 * estrategias tienen «Por qué» (la explicación de ese día); las demás del
 * registro, «Volver a X» (si quien mira puede volver). El último cambio, si dejó
 * el precio de hoy, se puede deshacer: «Volver a X (el de antes)» (ronda del 5-oct).
 */

const COLUMNAS = 6;

type Filtro = 'TODOS' | GrupoDeVia;

/**
 * Spec 275 (R1-e): ¿la fila del precio de hoy está entre las visibles? El mismo
 * criterio que marca la fila (`c === cambios[0]` y precio de hoy), sobre lo que
 * se ve: si el filtro de vía o de fecha esconde la fila de hoy, el bloque
 * plegado de arriba vuelve (auditoría, hallazgo 7).
 */
export function hayFilaDeHoy(d: Historial, visibles: CambioDelHistorial[]): boolean {
  return (
    d.cambios.length > 0 &&
    visibles.includes(d.cambios[0]) &&
    esElPrecioDeHoy(d.cambios[0], d.producto.currentPrice)
  );
}

export function HistorialDePrecios({
  productId,
  rango = CUALQUIER_FECHA,
  onRango,
  onVolver,
  version = 0,
  explicacionDeHoy,
  onHayFilaDeHoy,
}: {
  productId: string;
  /** «Cambiado», del calendario (vive en la ventana: también filtra los cambios de antes). */
  rango?: RangoDeFechas;
  onRango: (r: RangoDeFechas) => void;
  /**
   * «Volver a X»: sólo si quien mira puede volver (administradores, D15).
   * `aDonde`: al precio que dejó ese cambio, o al de antes (deshacer).
   */
  onVolver?: (c: CambioDelHistorial, h: Historial, aDonde: 'DESPUES' | 'ANTES') => void;
  /** Lote C2b (T4): cambia después de volver, para traer el historial otra vez. */
  version?: number;
  /** Spec 275 (R1-e): la explicación en vivo del precio de hoy, que la ventana monta; va en la fila de hoy. */
  explicacionDeHoy?: ReactNode;
  /** Spec 275 (R1-e): avisa si la fila de hoy está a la vista (la ventana esconde su bloque de arriba). */
  onHayFilaDeHoy?: (hay: boolean) => void;
}) {
  // Demo con «Entrar como»: volver a un precio cambia el de la persona (403 en el servidor).
  const soloMira = useSoporteSoloMira();
  const [datos, setDatos] = useState<Historial | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [intento, setIntento] = useState(0);
  const [filtro, setFiltro] = useState<Filtro>('TODOS');
  const [abierta, setAbierta] = useState<string | null>(null);
  // Spec 275 (R1-e): la fila apuntada por «Ver ese cambio», resaltada.
  const [resaltado, setResaltado] = useState<string | null>(null);
  // «Por qué» se pide al tocarlo: la explicación vive en la propuesta.
  const [propuestas, setPropuestas] = useState<PropuestaDelHistorial[] | null>(null);
  const [errorDePropuestas, setErrorDePropuestas] = useState(false);
  // Cuenta los pedidos de «Por qué»: la respuesta de otro producto (o de un
  // pedido viejo) se descarta.
  const pedido = useRef(0);

  useEffect(() => {
    let vivo = true;
    pedido.current += 1;
    setDatos(null);
    setError(null);
    setFiltro('TODOS');
    setAbierta(null);
    setPropuestas(null);
    setErrorDePropuestas(false);
    historialDePreciosApi
      .delProducto(productId)
      .then((r) => vivo && setDatos(r))
      .catch((e) => vivo && setError(e ?? true));
    return () => {
      vivo = false;
    };
  }, [productId, intento, version]);

  const pedirPropuestas = () => {
    setErrorDePropuestas(false);
    const n = ++pedido.current;
    priceProposalsApi
      .delProducto(productId)
      .then((r) => n === pedido.current && setPropuestas(r))
      .catch(() => n === pedido.current && setErrorDePropuestas(true));
  };

  const verPorQue = (c: CambioDelHistorial) => {
    const esta = abierta === c.id;
    setAbierta(esta ? null : c.id);
    // Spec 275 (R1-e): la fila de hoy sin propuesta y los VOLVER no la necesitan.
    if (esta || c.propuestaId == null || propuestas || errorDePropuestas) return;
    pedirPropuestas();
  };

  const hoy = datos?.producto.currentPrice ?? 0;
  const enFecha = datos ? datos.cambios.filter((c) => enElRango(c.cuando, rango)) : [];
  // Si la fecha deja sin cambios a la vía elegida, vale «Todos».
  const opciones = filtrosDelHistorial(enFecha);
  const vigente: Filtro = opciones.some((o) => o.valor === filtro) ? filtro : 'TODOS';
  const visibles = enFecha.filter((c) => vigente === 'TODOS' || GRUPO_DE_LA_VIA[c.via] === vigente);
  // Mientras carga (datos null) no hay fila de hoy: el bloque de arriba se ve
  // hasta saberlo (empieza en false, como dice el plan).
  const filaDeHoyVisible = datos ? hayFilaDeHoy(datos, visibles) : false;
  useEffect(() => {
    onHayFilaDeHoy?.(filaDeHoyVisible);
  }, [filaDeHoyVisible, onHayFilaDeHoy]);

  if (error)
    return (
      <section aria-label="Historial de precios">
        <EstadoError onReintentar={() => setIntento((n) => n + 1)}>
          {mensajeDeError(error, 'No se pudo traer el historial de precios.')}
        </EstadoError>
      </section>
    );

  if (!datos)
    return (
      <section aria-label="Historial de precios" className="space-y-2">
        <span role="status" aria-live="polite" className="sr-only">
          Cargando el historial de precios
        </span>
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-full" />
      </section>
    );

  const primeroDeAntes = visibles.findIndex((c) => c.deAntes);
  const filtrado = vigente !== 'TODOS' || rango.desde != null || rango.hasta != null;
  const e = datos.producto.estrategia;
  const ultimo = ultimoDelRegistro(datos.cambios);

  return (
    <section aria-label="Historial de precios" className="space-y-3">
      <p className="text-sm text-muted-foreground">
        {datos.producto.sku} · hoy{' '}
        <b className="font-semibold text-foreground">{formatoPrecio(hoy)}</b> ·{' '}
        {e ? `lo toma «${e.name}»` : 'ninguna estrategia lo toma'}
      </p>

      <div className="flex flex-wrap items-center gap-2">
        <GrupoDeOpciones
          aria-label="Vía"
          opciones={opciones}
          valor={vigente}
          onCambiar={setFiltro}
        />
        <div className="ml-auto">
          <SelectorDeFechas etiqueta="Cambiado" valor={rango} onCambiar={onRango} />
        </div>
      </div>

      {datos.cambios.length === 0 ? (
        <EstadoVacio titulo="Todavía no tiene cambios de precio registrados.">
          {datos.registroDesde ? textoDelCorte(datos.registroDesde) : null}
        </EstadoVacio>
      ) : visibles.length === 0 ? (
        <EstadoVacio
          titulo="Ninguno con esos filtros"
          accion={
            filtrado ? (
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setFiltro('TODOS');
                  onRango(CUALQUIER_FECHA);
                }}
              >
                Quitar los filtros
              </Button>
            ) : null
          }
        >
          {`Tiene ${datos.cambios.length === 1 ? '1 cambio' : `${datos.cambios.length} cambios`} en total.`}
        </EstadoVacio>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Cuándo</TableHead>
                <TableHead className="whitespace-nowrap text-right">Antes → después (Bs)</TableHead>
                <TableHead className="text-right">Cambio</TableHead>
                <TableHead>Quién o qué</TableHead>
                <TableHead>Motivo</TableHead>
                <TableHead>
                  <span className="sr-only">Acciones</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visibles.map((c, i) => {
                const { titulo, detalle } = quienOQue(c);
                const esta = abierta === c.id;
                const esHoy = c === datos.cambios[0] && esElPrecioDeHoy(c, hoy);
                const propuesta = propuestas?.find((p) => p.id === c.propuestaId) ?? null;
                // Spec 275 (R1-e): «Ver ese cambio» sólo si la fila apuntada está a la vista.
                const destino =
                  c.aCambioId != null && visibles.some((v) => v.id === c.aCambioId)
                    ? c.aCambioId
                    : null;
                return (
                  <Fragment key={c.id}>
                    {i === primeroDeAntes && datos.registroDesde && (
                      <TableRow className="hover:bg-transparent">
                        <TableCell
                          colSpan={COLUMNAS}
                          className="bg-muted/40 text-xs text-muted-foreground"
                        >
                          {textoDelCorte(datos.registroDesde)}
                        </TableCell>
                      </TableRow>
                    )}
                    <TableRow
                      id={`cambio-${c.id}`}
                      data-testid="cambio-del-historial"
                      data-resaltado={resaltado === c.id ? 'true' : undefined}
                      className={
                        resaltado === c.id
                          ? 'bg-muted/60 hover:bg-muted/60'
                          : esta
                            ? 'bg-muted/40 hover:bg-muted/40'
                            : undefined
                      }
                    >
                      <TableCell className="whitespace-nowrap align-top">
                        <span className={esHoy ? 'font-semibold' : undefined}>
                          {cuandoEnTexto(c.cuando)}
                        </span>
                        {esHoy && (
                          <span className="block text-xs text-muted-foreground">precio de hoy</span>
                        )}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-right align-top tabular-nums">
                        {c.antes != null ? (
                          <span className="text-muted-foreground">{formatoPrecio(c.antes)} → </span>
                        ) : (
                          <span className="text-muted-foreground">nació en </span>
                        )}
                        <Precio valor={c.despues} className="font-semibold" />
                      </TableCell>
                      <TableCell className="text-right align-top">
                        {c.antes != null ? (
                          <CambioDePrecio
                            diferencia={c.despues - c.antes}
                            porcentaje={
                              c.antes > 0 ? ((c.despues - c.antes) / c.antes) * 100 : null
                            }
                          />
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className="align-top">
                        <span className="block">{titulo}</span>
                        {detalle && (
                          <span className="block text-xs text-muted-foreground">{detalle}</span>
                        )}
                      </TableCell>
                      <TableCell className="max-w-xs align-top text-muted-foreground">
                        {c.motivo ? <TextoRecortable texto={`«${c.motivo}»`} /> : '—'}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-right align-top">
                        <div className="flex justify-end gap-2">
                          {tienePorQue(c, esHoy) && (
                            <Button
                              size="sm"
                              variant="outline"
                              aria-expanded={esta}
                              aria-label={`Por qué: cambio del ${cuandoEnTexto(c.cuando)}`}
                              onClick={() => verPorQue(c)}
                            >
                              Por qué
                            </Button>
                          )}
                          {onVolver && sePuedeVolver(c, hoy) && (
                            <Button
                              size="sm"
                              variant="outline"
                              aria-label={`${textoDeVolver(c)}: cambio del ${cuandoEnTexto(c.cuando)}`}
                              onClick={() => onVolver(c, datos, 'DESPUES')}
                              {...atributosSoloMira(soloMira)}
                            >
                              {textoDeVolver(c)}
                            </Button>
                          )}
                          {onVolver && sePuedeDeshacer(c, hoy, c === ultimo) && (
                            <Button
                              size="sm"
                              variant="outline"
                              aria-label={`${textoDeDeshacer(c)}: cambio del ${cuandoEnTexto(c.cuando)}`}
                              onClick={() => onVolver(c, datos, 'ANTES')}
                              {...atributosSoloMira(soloMira)}
                            >
                              {textoDeDeshacer(c)}
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                    {esta && (
                      <TableRow className="bg-muted/40 hover:bg-muted/40">
                        <TableCell colSpan={COLUMNAS} className="px-3 pb-3 pt-1">
                          {esHoy ? (
                            <>
                              {explicacionDeHoy}
                              {propuesta ? (
                                <>
                                  <h4 className="mt-3 text-sm font-semibold">
                                    Cómo se calculó el {cuandoEnTexto(c.cuando)}
                                  </h4>
                                  <PorQueEstePrecio
                                    recorrido={desdeFilaDeLaBandeja(propuesta)}
                                    tiempo="PASO"
                                    fecha={propuesta.resolvedAt ?? propuesta.createdAt}
                                  />
                                </>
                              ) : c.propuestaId ? (
                                errorDePropuestas ? (
                                  // Auditoría H7: un pedido que falló no es «no se encontró».
                                  <EstadoError onReintentar={pedirPropuestas}>
                                    No se pudo traer la explicación.
                                  </EstadoError>
                                ) : propuestas ? (
                                  <p className="text-sm text-muted-foreground">
                                    No se encontró la explicación de este cambio.
                                  </p>
                                ) : (
                                  <>
                                    <span role="status" className="sr-only">
                                      Cargando la explicación
                                    </span>
                                    <Skeleton className="h-16 w-full" />
                                  </>
                                )
                              ) : null}
                            </>
                          ) : c.via === 'VOLVER' ? (
                            <dl
                              data-testid="por-que-del-volver"
                              className="grid grid-cols-[8rem_1fr] gap-x-4 text-sm"
                            >
                              <dt className="font-semibold">Qué pasó</dt>
                              <dd className="min-w-0 [overflow-wrap:anywhere]">
                                {queHizoElVolver(c)}
                              </dd>
                              <dt className="font-semibold">Quién</dt>
                              <dd className="min-w-0 [overflow-wrap:anywhere]">
                                {c.quien ?? 'No quedó registrado'}
                              </dd>
                              <dt className="font-semibold">Cuándo</dt>
                              <dd className="min-w-0 [overflow-wrap:anywhere]">
                                {cuandoEnTexto(c.cuando)}
                              </dd>
                              <dt className="font-semibold">Precio</dt>
                              <dd className="min-w-0 [overflow-wrap:anywhere]">
                                {c.antes != null ? formatoPrecio(c.antes) : '—'} →{' '}
                                {formatoPrecio(c.despues)}
                              </dd>
                              <dt className="font-semibold">Motivo</dt>
                              <dd className="min-w-0 [overflow-wrap:anywhere]">
                                {c.motivo ? `«${c.motivo}»` : 'Sin motivo escrito'}
                              </dd>
                              {destino && (
                                <dd className="min-w-0 [overflow-wrap:anywhere]">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                      setResaltado(destino);
                                      document
                                        .getElementById(`cambio-${destino}`)
                                        ?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
                                    }}
                                  >
                                    Ver ese cambio
                                  </Button>
                                </dd>
                              )}
                            </dl>
                          ) : propuesta ? (
                            <PorQueEstePrecio
                              recorrido={desdeFilaDeLaBandeja(propuesta)}
                              tiempo="PASO"
                              fecha={propuesta.resolvedAt ?? propuesta.createdAt}
                            />
                          ) : errorDePropuestas ? (
                            // Auditoría H7: un pedido que falló no es «no se encontró».
                            <EstadoError onReintentar={pedirPropuestas}>
                              No se pudo traer la explicación.
                            </EstadoError>
                          ) : propuestas ? (
                            <p className="text-sm text-muted-foreground">
                              No se encontró la explicación de este cambio.
                            </p>
                          ) : (
                            <>
                              <span role="status" className="sr-only">
                                Cargando la explicación
                              </span>
                              <Skeleton className="h-16 w-full" />
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })}
              {primeroDeAntes === -1 && datos.registroDesde && (
                <TableRow className="hover:bg-transparent">
                  <TableCell
                    colSpan={COLUMNAS}
                    className="bg-muted/40 text-xs text-muted-foreground"
                  >
                    {textoDelCorte(datos.registroDesde)}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}

      <p className="text-xs text-muted-foreground">
        Cada cambio queda guardado; volver a un precio agrega uno nuevo y no borra nada.
        {datos.recortado && ' Se muestran los últimos 200 de cada fuente.'}
      </p>
    </section>
  );
}
